// GtForms — the Forms screen. A library of saved forms; for each one a
// builder with a live preview, live responses (charts, every person's answers,
// export and reminders), and the form's own shared link with tracking.
// Forms are sent by email from Candidates, a bench or a role pipeline (tick
// people, then Send form), or shared by link from here.
// C1-SHARECHECK: "Put forward for a role" questions carry a role, so the answer
// is kept as a record against that role. Nothing is gated on it.
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import API_BASE from '../../config';
import { GtSelect } from '../../components/GtControls';
import GtFormFields, { QTYPES, QTYPE, HAS_OPTIONS, QIcon, GtAnswerList, answerText } from '../../components/GtFormFields';

const JSON_HEADERS = { 'Content-Type': 'application/json' };
const plural = (n, one, many) => (n || 0) + ' ' + (n === 1 ? one : many);
const newKey = () => 'q' + Math.random().toString(16).slice(2, 10);

function ukDate(v) {
  if (!v) return '';
  const d = new Date(v);
  return isNaN(d) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
function relTime(v) {
  if (!v) return '';
  const s = Math.floor((Date.now() - new Date(v).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return Math.floor(s / 60) + ' min ago';
  if (s < 86400) return Math.floor(s / 3600) + ' hr ago';
  if (s < 2 * 86400) return 'yesterday';
  if (s < 30 * 86400) return Math.floor(s / 86400) + ' days ago';
  return ukDate(v);
}
const AVC = ['#1E5A7B', '#2E7D32', '#6A1B9A', '#B8860B', '#00695C', '#C2410C', '#374151', '#9D174D', '#1565C0', '#4E342E', '#0F766E', '#3949AB'];
function avatarColour(name) { const s = String(name || '?'); let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return AVC[h % AVC.length]; }
function initials(name) { return String(name || '?').trim().split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?'; }

function copyText(t) {
  try { if (navigator.clipboard) return navigator.clipboard.writeText(t); } catch (e) { /* fall through */ }
  const a = document.createElement('textarea');
  a.value = t; document.body.appendChild(a); a.select();
  try { document.execCommand('copy'); } catch (e) { /* ignore */ }
  document.body.removeChild(a);
  return Promise.resolve();
}

const I = {
  back: 'M15 18l-6-6 6-6',
  plus: 'M12 5v14 M5 12h14',
  copy: 'M9 9h10v10H9z M5 15V5h10',
  trash: 'M4 7h16 M10 11v6 M14 11v6 M6 7l1 13h10l1-13 M9 7V4h6v3',
  up: 'M12 19V5 M6 11l6-6 6 6',
  down: 'M12 5v14 M6 13l6 6 6-6',
  grip: 'M9 6h.01 M15 6h.01 M9 12h.01 M15 12h.01 M9 18h.01 M15 18h.01',
  link: 'M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1 M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1',
  send: 'M22 2 11 13 M22 2l-7 20-4-9-9-4 20-7z',
  form: 'M9 3h6a1 1 0 0 1 1 1v1H8V4a1 1 0 0 1 1-1z M8 5H6a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2 M8 11h8 M8 15h5',
  chev: 'M6 9l6 6 6-6',
  x: 'M6 6l12 12 M18 6 6 18',
  open: 'M14 4h6v6 M20 4l-9 9 M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5',
  dl: 'M12 4v11 M7 10l5 5 5-5 M5 20h14',
  bell: 'M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9 M10 21a2 2 0 0 0 4 0',
};

// ── Starting points ─────────────────────────────────────────────────────────
const q = (type, label, extra) => ({ key: newKey(), type, label, help: '', required: false, ...(extra || {}) });
const TEMPLATES = [
  { id: 'blank', name: 'Blank form', blurb: 'Start from nothing.', title: '', questions: () => [] },
  {
    id: 'availability', name: 'Availability check', blurb: 'Who is free, from when, and for how much time.', title: 'Availability check',
    questions: () => [
      q('yesno', 'Are you available for new work?', { required: true }),
      q('daterange', 'When are you available?', { help: 'Your earliest start and the latest you could carry on.' }),
      q('choice', 'How many hours a week could you give?', { options: ['Under 10', '10 to 20', '20 to 30', 'Full time'] }),
      q('choice', 'How would you like to work?', { options: ['Remote', 'Hybrid', 'On site'] }),
      q('paragraph', 'Anything else we should know?'),
    ],
  },
  {
    id: 'interest', name: 'Interest in a role', blurb: 'Gauge interest, expectations and notice.', title: 'Interest in a role',
    questions: () => [
      q('yesno', 'Would you like us to put you forward?', { required: true }),
      q('scale', 'How interested are you?', { min: 1, max: 5, min_label: 'Not really', max_label: 'Very' }),
      q('number', 'Expected salary or day rate (GBP)'),
      q('choice', 'Notice period', { options: ['Immediate', '1 week', '2 weeks', '1 month', '2 months', '3 months or more'] }),
      q('paragraph', 'Any questions for us?'),
    ],
  },
  {
    id: 'project', name: 'Project interest', blurb: 'Line people up for an upcoming project.', title: 'Project interest',
    questions: () => [
      q('yesno', 'Would you like to be considered for this project?', { required: true }),
      q('date', 'Earliest date you could start'),
      q('choice', 'Hours a week you could commit', { options: ['Under 10', '10 to 20', '20 to 40', '40 or more'] }),
      q('url', 'Portfolio, GitHub or LinkedIn'),
      q('paragraph', 'Relevant experience in a few lines'),
    ],
  },
];

function Confirm({ title, body, yes, danger, onYes, onNo }) {
  return (
    <div className="gtc-modal-backdrop" onClick={onNo}>
      <div className="gtc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="gtc-modal-head">{title}</div>
        <div className="gtc-modal-body"><p className="gtfm-confirm">{body}</p></div>
        <div className="gtc-modal-foot">
          <button type="button" className="gtc-btn-ghost" onClick={onNo}>Cancel</button>
          <button type="button" className={'gtc-primary' + (danger ? ' gtfm-danger' : '')} onClick={onYes}>{yes}</button>
        </div>
      </div>
    </div>
  );
}

// ── Screen ─────────────────────────────────────────────────────────────────
export default function GtForms({ getToken }) {
  const { formId } = useParams();
  const [toast, setToast] = useState(null);
  const flash = useCallback((m) => { setToast(m); setTimeout(() => setToast(null), 2600); }, []);
  const authFetch = useCallback(async (path, opts = {}) => {
    const token = await getToken();
    const res = await fetch(API_BASE + path, { ...opts, headers: { ...(opts.headers || {}), Authorization: 'Bearer ' + token } });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.detail || body.message || body.error || ('request failed: ' + res.status));
    return body;
  }, [getToken]);

  return (
    <>
      {formId ? <FormRecord key={formId} formId={formId} authFetch={authFetch} flash={flash} />
        : <FormsLibrary authFetch={authFetch} flash={flash} />}
      {toast && <div className="gtfm-toast">{toast}</div>}
    </>
  );
}

// ── Library ────────────────────────────────────────────────────────────────
function FormsLibrary({ authFetch, flash }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState('active');
  const [forms, setForms] = useState(null);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [qtext, setQtext] = useState('');

  const load = useCallback(async () => {
    try { const r = await authFetch('/api/forms?status=' + status); setForms(r.forms || []); setError(null); }
    catch (e) { setError(e.message); }
  }, [authFetch, status]);
  useEffect(() => { setForms(null); load(); }, [load]);

  async function duplicate(f, e) {
    e.stopPropagation();
    try { const r = await authFetch('/api/forms/' + f.id + '/duplicate', { method: 'POST', headers: JSON_HEADERS, body: '{}' }); flash('Copied'); navigate('/forms/' + r.id); }
    catch (err) { flash(err.message); }
  }

  const shown = (forms || []).filter((f) => !qtext.trim() || f.title.toLowerCase().indexOf(qtext.trim().toLowerCase()) !== -1);

  return (
    <div className="gtc-wrap gtfm">
      <div className="gtc-head">
        <div>
          <h1 className="gtc-title">Forms</h1>
          <p className="gtc-sub">Build a form once, send it to any group or share its link. Every answer lands on the person's record.</p>
        </div>
        <div className="gtc-head-right">
          <button type="button" className="gtc-primary" onClick={() => setCreating(true)}>New form</button>
        </div>
      </div>

      <div className="gtfm-libbar">
        <div className="gtfm-seg" role="tablist">
          {[['active', 'Active'], ['archived', 'Archived']].map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={status === k} className={'gtfm-seg-b' + (status === k ? ' gtfm-seg-b--on' : '')} onClick={() => setStatus(k)}>{l}</button>
          ))}
        </div>
        <input className="gtsb-search gtfm-search" placeholder="Search forms" value={qtext} onChange={(e) => setQtext(e.target.value)} />
      </div>

      {error && <div className="gtnt-banner gtnt-banner--rose">{error}</div>}
      {forms === null && !error && <div className="gtfm-skel">{[0, 1, 2].map((i) => <div key={i} className="gtfm-skel-row" />)}</div>}

      {forms && forms.length === 0 && status === 'active' && (
        <div className="gtfm-empty">
          <div className="gtfm-empty-ico"><QIcon d={I.form} cls="gtfm-ico-lg" /></div>
          <h3>No forms yet</h3>
          <p>Ask people about availability, interest in a role or a project, or anything else. Start from one of these.</p>
          <div className="gtfm-tpls gtfm-tpls--inline">
            {TEMPLATES.map((t) => (
              <button type="button" key={t.id} className="gtfm-tpl" onClick={() => setCreating(t.id)}>
                <b>{t.name}</b><span>{t.blurb}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {forms && forms.length === 0 && status === 'archived' && <div className="gtfm-none">Nothing archived.</div>}

      {forms && forms.length > 0 && (
        <div className="gtfm-list">
          {shown.map((f) => {
            const rate = f.asked ? Math.round((f.answered_email / f.asked) * 100) : null;
            return (
              <div key={f.id} role="button" tabIndex={0} className="gtfm-card" onClick={() => navigate('/forms/' + f.id)}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate('/forms/' + f.id); }}>
                <div className="gtfm-card-ico"><QIcon d={I.form} /></div>
                <div className="gtfm-card-main">
                  <div className="gtfm-card-title">{f.title}</div>
                  <div className="gtfm-card-meta">
                    <span>{plural(f.question_count, 'question', 'questions')}</span>
                    {f.sends > 0 && <span>Sent {f.sends === 1 ? 'once' : f.sends + ' times'}{f.last_sent_at ? ', last ' + relTime(f.last_sent_at) : ''}</span>}
                    {f.link_enabled && <span className="gtfm-tag gtfm-tag--teal"><QIcon d={I.link} cls="gtfm-ico-xs" />Link on</span>}
                    {f.sends === 0 && !f.via_link && <span className="gtfm-tag">Not sent yet</span>}
                  </div>
                </div>
                <div className="gtfm-card-stats">
                  <div className="gtfm-card-big"><b>{f.answered}</b><span>{f.answered === 1 ? 'answer' : 'answers'}</span></div>
                  {rate !== null && (
                    <div className="gtfm-card-rate" title={f.answered_email + ' of ' + f.asked + ' emailed have answered'}>
                      <div className="gtfm-minibar"><i style={{ width: rate + '%' }} /></div>
                      <span>{rate}% of {f.asked} emailed</span>
                    </div>
                  )}
                  {f.last_answer_at && <span className="gtfm-card-last">Last answer {relTime(f.last_answer_at)}</span>}
                </div>
                <button type="button" className="gtfm-iconbtn" title="Duplicate" aria-label="Duplicate" onClick={(e) => duplicate(f, e)}><QIcon d={I.copy} /></button>
              </div>
            );
          })}
          {shown.length === 0 && <div className="gtfm-none">No forms match.</div>}
        </div>
      )}

      {creating && <NewFormModal initial={typeof creating === 'string' ? creating : 'blank'} authFetch={authFetch} onClose={() => setCreating(false)}
        onDone={(id) => { setCreating(false); navigate('/forms/' + id); }} />}
    </div>
  );
}

function NewFormModal({ initial, authFetch, onClose, onDone }) {
  const [tpl, setTpl] = useState(initial || 'blank');
  const t = TEMPLATES.find((x) => x.id === tpl) || TEMPLATES[0];
  const [title, setTitle] = useState(t.title);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const pick = (id) => { setTpl(id); const x = TEMPLATES.find((y) => y.id === id); if (!touched) setTitle(x.title); };
  async function create() {
    if (!title.trim()) { setError('Give the form a title.'); return; }
    setBusy(true); setError(null);
    try {
      const r = await authFetch('/api/forms', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ title: title.trim(), questions: t.questions() }) });
      onDone(r.id);
    } catch (e) { setError(e.message); setBusy(false); }
  }
  return (
    <div className="gtc-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="gtc-modal gtc-modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="gtc-modal-head">New form</div>
        <div className="gtc-modal-body">
          {error && <div className="gtnt-banner gtnt-banner--rose">{error}</div>}
          <label className="gtc-field"><span>Title</span>
            <input autoFocus value={title} maxLength={120} placeholder="e.g. Availability for the March intake"
              onChange={(e) => { setTitle(e.target.value); setTouched(true); }} onKeyDown={(e) => { if (e.key === 'Enter') create(); }} />
          </label>
          <div className="gtc-field"><span>Start from</span>
            <div className="gtfm-tpls">
              {TEMPLATES.map((x) => (
                <button type="button" key={x.id} className={'gtfm-tpl' + (tpl === x.id ? ' gtfm-tpl--on' : '')} onClick={() => pick(x.id)}>
                  <b>{x.name}</b><span>{x.blurb}</span>
                  {x.id !== 'blank' && <em>{plural(x.questions().length, 'question', 'questions')}</em>}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="gtc-modal-foot">
          <button type="button" className="gtc-btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className="gtc-primary" disabled={busy} onClick={create}>{busy ? 'Creating…' : 'Create form'}</button>
        </div>
      </div>
    </div>
  );
}

// ── One form ───────────────────────────────────────────────────────────────
function FormRecord({ formId, authFetch, flash }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const tab = params.get('tab') || (params.get('scope') ? 'responses' : 'build');
  const setTab = (t) => { const p = new URLSearchParams(params); p.set('tab', t); if (t !== 'responses') p.delete('scope'); setParams(p, { replace: true }); };

  const load = useCallback(async () => {
    try { setData(await authFetch('/api/forms/' + formId)); setError(null); }
    catch (e) { setError(e.message); }
  }, [authFetch, formId]);
  useEffect(() => { load(); }, [load]);

  if (error && !data) return <div className="gtc-wrap gtfm"><div className="gtnt-banner gtnt-banner--rose">{error}</div></div>;
  if (!data) return <div className="gtc-wrap gtfm"><div className="gtfm-skel"><div className="gtfm-skel-row" /><div className="gtfm-skel-row" /></div></div>;
  const f = data.form;
  const archived = f.status === 'archived';
  const totalAnswers = data.sends.reduce((a, s) => a + s.answered, 0) + (data.link.responses || 0);

  async function act(kind) {
    try {
      if (kind === 'duplicate') {
        const r = await authFetch('/api/forms/' + f.id + '/duplicate', { method: 'POST', headers: JSON_HEADERS, body: '{}' });
        flash('Copied'); navigate('/forms/' + r.id); return;
      }
      if (kind === 'archive' || kind === 'restore') {
        await authFetch('/api/forms/' + f.id, { method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify({ status: kind === 'archive' ? 'archived' : 'active' }) });
        flash(kind === 'archive' ? 'Archived' : 'Restored'); load(); return;
      }
      if (kind === 'delete') {
        await authFetch('/api/forms/' + f.id, { method: 'DELETE' });
        flash('Deleted'); navigate('/forms');
      }
    } catch (e) { flash(e.message); }
  }

  return (
    <div className="gtc-wrap gtfm gtfm--record">
      <button type="button" className="gtfm-back" onClick={() => navigate('/forms')}><QIcon d={I.back} cls="gtfm-ico-sm" />Forms</button>
      <div className="gtfm-rhead">
        <div className="gtfm-rhead-ico"><QIcon d={I.form} /></div>
        <div className="gtfm-rhead-main">
          <h1 className="gtc-title">{f.title}{archived && <span className="gtfm-tag gtfm-tag--slate">Archived</span>}</h1>
          <p className="gtc-sub">
            {plural(f.questions.filter((x) => x.type !== 'section').length, 'question', 'questions')}
            {' · '}{plural(totalAnswers, 'answer', 'answers')}
            {f.link_enabled && ' · Link on'}
            {f.created_by_name ? ' · Created by ' + f.created_by_name : ''}
          </p>
        </div>
        <div className="gtfm-rhead-act">
          <button type="button" className="gtc-btn-ghost" onClick={() => act('duplicate')}>Duplicate</button>
          {archived
            ? <button type="button" className="gtc-btn-ghost" onClick={() => act('restore')}>Restore</button>
            : <button type="button" className="gtc-btn-ghost" onClick={() => setConfirm({ title: 'Archive this form?', body: 'It leaves the list and can no longer be sent or answered. Answers already given stay on people\'s records. You can restore it at any time.', yes: 'Archive', run: () => act('archive') })}>Archive</button>}
          {!f.has_requests && <button type="button" className="gtc-btn-ghost gtr-danger" onClick={() => setConfirm({ title: 'Delete this form?', body: 'Nobody has been sent it yet, so it can be deleted. This cannot be undone.', yes: 'Delete', danger: true, run: () => act('delete') })}>Delete</button>}
        </div>
      </div>

      <div className="gtfm-tabs" role="tablist">
        {[['build', 'Questions'], ['responses', 'Responses' + (totalAnswers ? ' · ' + totalAnswers : '')], ['share', 'Share and send']].map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={'gtfm-tab' + (tab === k ? ' gtfm-tab--on' : '')} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>

      {tab === 'build' && <Builder form={f} authFetch={authFetch} flash={flash} onSaved={load} sent={data.sends.length > 0 || f.has_requests} />}
      {tab === 'responses' && <Responses form={f} sends={data.sends} link={data.link} authFetch={authFetch} flash={flash} onChanged={load} />}
      {tab === 'share' && <Share form={f} sends={data.sends} link={data.link} authFetch={authFetch} flash={flash} onChanged={load}
        openScope={(s) => { const p = new URLSearchParams(); p.set('tab', 'responses'); p.set('scope', s); setParams(p, { replace: true }); }} />}

      {confirm && <Confirm {...confirm} onNo={() => setConfirm(null)} onYes={() => { const r = confirm.run; setConfirm(null); r(); }} />}
    </div>
  );
}

// ── Builder ────────────────────────────────────────────────────────────────
function TypePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const off = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', off); window.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', off); window.removeEventListener('keydown', esc); };
  }, [open]);
  const t = QTYPE[value] || QTYPES[0];
  return (
    <div className="gtfm-tp" ref={ref}>
      <button type="button" className="gtfm-tp-btn" onClick={() => setOpen(!open)} aria-haspopup="listbox" aria-expanded={open}>
        <QIcon d={t.ico} cls="gtfm-ico-sm" /><span>{t.label}</span><QIcon d={I.chev} cls="gtfm-ico-xs" />
      </button>
      {open && (
        <div className="gtfm-tp-menu" role="listbox">
          {QTYPES.filter((x) => x.type !== 'section').map((x) => (
            <button type="button" key={x.type} role="option" aria-selected={x.type === value}
              className={'gtfm-tp-opt' + (x.type === value ? ' gtfm-tp-opt--on' : '')} onClick={() => { onChange(x.type); setOpen(false); }}>
              <QIcon d={x.ico} cls="gtfm-ico-sm" />{x.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Switch({ on, onChange, label }) {
  return (
    <label className="gtfm-switch">
      <button type="button" role="switch" aria-checked={!!on} className={'gtfm-sw' + (on ? ' gtfm-sw--on' : '')} onClick={() => onChange(!on)}><i /></button>
      <span>{label}</span>
    </label>
  );
}

function OptionsEditor({ q, set }) {
  const opts = q.options || [];
  const refs = useRef([]);
  const update = (i, v) => set({ options: opts.map((o, j) => (j === i ? v : o)) });
  const add = () => { set({ options: opts.concat(['Option ' + (opts.length + 1)]) }); setTimeout(() => { const el = refs.current[opts.length]; if (el) { el.focus(); el.select(); } }, 0); };
  const remove = (i) => set({ options: opts.filter((_, j) => j !== i) });
  const mark = q.type === 'checkboxes' ? 'gtfm-mk gtfm-mk--box' : q.type === 'dropdown' ? 'gtfm-mk gtfm-mk--num' : 'gtfm-mk';
  return (
    <div className="gtfm-opts">
      {opts.map((o, i) => (
        <div key={i} className="gtfm-opt">
          <span className={mark}>{q.type === 'dropdown' ? i + 1 : ''}</span>
          <input ref={(el) => { refs.current[i] = el; }} value={o} maxLength={150} onChange={(e) => update(i, e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
          <button type="button" className="gtfm-iconbtn" aria-label="Remove option" onClick={() => remove(i)}><QIcon d={I.x} cls="gtfm-ico-sm" /></button>
        </div>
      ))}
      {q.other && (
        <div className="gtfm-opt gtfm-opt--other">
          <span className={mark} /><span className="gtfm-other-l">Other, with their own answer</span>
          <button type="button" className="gtfm-iconbtn" aria-label="Remove Other" onClick={() => set({ other: false })}><QIcon d={I.x} cls="gtfm-ico-sm" /></button>
        </div>
      )}
      <div className="gtfm-opt gtfm-opt--add">
        <span className={mark} />
        <button type="button" className="gtfm-addopt" onClick={add}>Add option</button>
        {q.type !== 'dropdown' && !q.other && <><span className="gtfm-or">or</span><button type="button" className="gtfm-addopt gtfm-addopt--teal" onClick={() => set({ other: true })}>add "Other"</button></>}
      </div>
    </div>
  );
}

function ScaleEditor({ q, set }) {
  const tos = [];
  for (let i = 2; i <= 10; i++) tos.push({ value: i, label: String(i) });
  return (
    <div className="gtfm-scale">
      <div className="gtfm-scale-row">
        <div className="gtfm-scale-sel"><GtSelect value={q.min} options={[{ value: 0, label: '0' }, { value: 1, label: '1' }]} onChange={(v) => set({ min: Number(v) })} /></div>
        <span>to</span>
        <div className="gtfm-scale-sel"><GtSelect value={q.max} options={tos} onChange={(v) => set({ max: Number(v) })} /></div>
      </div>
      <div className="gtfm-scale-labels">
        <label><span>{q.min}</span><input value={q.min_label || ''} maxLength={60} placeholder="Label (optional)" onChange={(e) => set({ min_label: e.target.value })} /></label>
        <label><span>{q.max}</span><input value={q.max_label || ''} maxLength={60} placeholder="Label (optional)" onChange={(e) => set({ max_label: e.target.value })} /></label>
      </div>
    </div>
  );
}

function summaryLine(q) {
  if (q.type === 'put_forward') return 'Yes or no · ' + (q.role_title ? 'consent for ' + q.role_title : 'no role picked yet');
  if (HAS_OPTIONS[q.type]) return (q.options || []).slice(0, 4).join(' · ') + ((q.options || []).length > 4 ? ' · …' : '') + (q.other ? ' · Other' : '');
  if (q.type === 'scale') return q.min + ' to ' + q.max + (q.min_label || q.max_label ? ' · ' + [q.min_label, q.max_label].filter(Boolean).join(' to ') : '');
  return QTYPE[q.type] ? QTYPE[q.type].label : '';
}

function RolePicker({ q, set, roleOpts }) {
  const opts = [{ value: '', label: roleOpts ? 'Pick the role' : 'Loading roles…' }].concat((roleOpts || []).map((r) => ({
    value: String(r.id), label: r.title + (r.client_name ? ' · ' + r.client_name : '') + (r.status && ['open', 'active', 'live', 'draft'].indexOf(r.status) === -1 ? ' (' + r.status + ')' : ''),
  })));
  return (
    <div className="gtsc-qrole">
      <span className="gtsc-qrole-l">Role</span>
      <div className="gtsc-qrole-sel">
        <GtSelect value={q.role_id ? String(q.role_id) : ''} options={opts} onChange={(v) => {
          const r = (roleOpts || []).find((x) => String(x.id) === String(v));
          set({ role_id: v ? Number(v) : null, role_title: r ? r.title : '' });
        }} />
      </div>
      <p className="gtsc-qrole-help">Their yes or no is kept on their record against this role. People see your question, never the client's name.</p>
    </div>
  );
}

function QuestionCard({ q, n, active, onActivate, set, onMove, onCopy, onRemove, first, last, drag, roleOpts }) {
  const isSection = q.type === 'section';
  const changeType = (type) => {
    const patch = { type };
    if (type === 'put_forward' && !q.label) patch.label = 'Are you happy for us to put you forward for this role?';
    if (HAS_OPTIONS[type] && !(q.options && q.options.length)) patch.options = ['Option 1'];
    if (type === 'dropdown') patch.other = false;
    if (type === 'scale' && q.max == null) Object.assign(patch, { min: 1, max: 5, min_label: '', max_label: '' });
    set(patch);
  };
  return (
    <div className={'gtfm-q' + (active ? ' gtfm-q--on' : '') + (isSection ? ' gtfm-q--section' : '') + (drag.over ? ' gtfm-q--over' : '')}
      onClick={() => !active && onActivate()} onDragOver={drag.onOver} onDrop={drag.onDrop}>
      <div className="gtfm-q-grip" draggable onDragStart={drag.onStart} onDragEnd={drag.onEnd} title="Drag to reorder"><QIcon d={I.grip} cls="gtfm-ico-sm" /></div>
      {!active ? (
        <div className="gtfm-q-sum">
          <span className="gtfm-q-n">{isSection ? <QIcon d={QTYPE.section.ico} cls="gtfm-ico-sm" /> : n}</span>
          <div className="gtfm-q-sumtext">
            <b>{q.label || (isSection ? 'Untitled section' : 'Untitled question')}{q.required && <i className="gtfm-req">*</i>}</b>
            <span><QIcon d={(QTYPE[q.type] || QTYPES[0]).ico} cls="gtfm-ico-xs" />{summaryLine(q)}</span>
          </div>
        </div>
      ) : (
        <div className="gtfm-q-edit">
          <div className="gtfm-q-top">
            <input className={'gtfm-q-label' + (isSection ? ' gtfm-q-label--section' : '')} autoFocus value={q.label} maxLength={300}
              placeholder={isSection ? 'Section title' : 'Question'} onChange={(e) => set({ label: e.target.value })} />
            {!isSection && <TypePicker value={q.type} onChange={changeType} />}
          </div>
          <input className="gtfm-q-help" value={q.help || ''} maxLength={500} placeholder={isSection ? 'Description (optional)' : 'Help text (optional)'}
            onChange={(e) => set({ help: e.target.value })} />
          {HAS_OPTIONS[q.type] && <OptionsEditor q={q} set={set} />}
          {q.type === 'scale' && <ScaleEditor q={q} set={set} />}
          {q.type === 'put_forward' && <RolePicker q={q} set={set} roleOpts={roleOpts} />}
          {!isSection && !HAS_OPTIONS[q.type] && q.type !== 'scale' && q.type !== 'put_forward' && (
            <div className="gtfm-q-sample">{(QTYPE[q.type] || {}).label} answer</div>
          )}
          <div className="gtfm-q-foot">
            <div className="gtfm-q-tools">
              <button type="button" className="gtfm-iconbtn" title="Move up" aria-label="Move up" disabled={first} onClick={() => onMove(-1)}><QIcon d={I.up} cls="gtfm-ico-sm" /></button>
              <button type="button" className="gtfm-iconbtn" title="Move down" aria-label="Move down" disabled={last} onClick={() => onMove(1)}><QIcon d={I.down} cls="gtfm-ico-sm" /></button>
              <button type="button" className="gtfm-iconbtn" title="Duplicate" aria-label="Duplicate" onClick={onCopy}><QIcon d={I.copy} cls="gtfm-ico-sm" /></button>
              <button type="button" className="gtfm-iconbtn gtfm-iconbtn--danger" title="Delete" aria-label="Delete" onClick={onRemove}><QIcon d={I.trash} cls="gtfm-ico-sm" /></button>
            </div>
            {!isSection && <Switch on={q.required} onChange={(v) => set({ required: v })} label="Required" />}
          </div>
        </div>
      )}
    </div>
  );
}

const snapshot = (d) => JSON.stringify({ title: d.title, intro: d.intro || '', thanks: d.thanks || '', questions: d.questions });

function Builder({ form, authFetch, flash, onSaved, sent }) {
  const [draft, setDraft] = useState(() => ({ title: form.title, intro: form.intro || '', thanks: form.thanks || '', questions: form.questions }));
  const [saved, setSaved] = useState(() => snapshot(form));
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [dragFrom, setDragFrom] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [preview, setPreview] = useState({});
  const [roleOpts, setRoleOpts] = useState(null);
  const dirty = snapshot(draft) !== saved;
  const needRoles = draft.questions.some((x) => x.type === 'put_forward');
  useEffect(() => {
    if (!needRoles || roleOpts) return;
    authFetch('/api/role-options').then((r) => setRoleOpts(r.roles || [])).catch(() => setRoleOpts([]));
  }, [needRoles, roleOpts, authFetch]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const qs = draft.questions;
  const setQs = (list) => setDraft((d) => ({ ...d, questions: list }));
  const setQ = (i, patch) => setQs(qs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const insertAt = (item) => {
    const at = active == null ? qs.length : Math.min(active + 1, qs.length);
    setQs(qs.slice(0, at).concat([item], qs.slice(at)));
    setActive(at);
  };
  const addQuestion = () => insertAt({ key: newKey(), type: 'choice', label: '', help: '', required: false, options: ['Option 1'], other: false });
  const addSection = () => insertAt({ key: newKey(), type: 'section', label: '', help: '' });
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= qs.length) return;
    const list = qs.slice(); const t = list[i]; list[i] = list[j]; list[j] = t;
    setQs(list); setActive(j);
  };
  const copy = (i) => {
    const c = { ...qs[i], key: newKey(), options: qs[i].options ? qs[i].options.slice() : undefined };
    setQs(qs.slice(0, i + 1).concat([c], qs.slice(i + 1))); setActive(i + 1);
  };
  const remove = (i) => { setQs(qs.filter((_, j) => j !== i)); setActive(null); };
  const drop = (to) => {
    if (dragFrom == null || dragFrom === to) { setDragFrom(null); setDragOver(null); return; }
    const list = qs.slice(); const [m] = list.splice(dragFrom, 1); list.splice(to, 0, m);
    setQs(list); setActive(to); setDragFrom(null); setDragOver(null);
  };

  async function save() {
    if (!draft.title.trim()) { setError('Give the form a title.'); return; }
    setBusy(true); setError(null);
    try {
      await authFetch('/api/forms/' + form.id, { method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify(draft) });
      setSaved(snapshot(draft)); flash('Saved'); onSaved();
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  let n = 0;
  return (
    <div className="gtfm-build">
      <div className="gtfm-editor">
        {sent && <div className="gtfm-note">People already sent this form keep the questions they were sent. Changes here apply to new sends and the shared link.</div>}
        {error && <div className="gtnt-banner gtnt-banner--rose">{error}</div>}
        <div className={'gtfm-head-card' + (active === -1 ? ' gtfm-q--on' : '')} onClick={() => setActive(-1)}>
          <input className="gtfm-title-in" value={draft.title} maxLength={120} placeholder="Form title" onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          <textarea className="gtfm-intro-in" rows={2} value={draft.intro} maxLength={4000} placeholder="Tell people what this is for. Shown at the top of the form."
            onChange={(e) => setDraft({ ...draft, intro: e.target.value })} />
          {active === -1 && (
            <label className="gtfm-thanks"><span>Thank-you message</span>
              <textarea rows={2} value={draft.thanks} maxLength={1000} placeholder="Shown after they submit. Leave blank for a short default."
                onChange={(e) => setDraft({ ...draft, thanks: e.target.value })} />
            </label>
          )}
        </div>

        {qs.map((x, i) => {
          if (x.type !== 'section') n += 1;
          return (
            <QuestionCard key={x.key} q={x} n={n} active={active === i} onActivate={() => setActive(i)}
              set={(p) => setQ(i, p)} onMove={(d) => move(i, d)} onCopy={() => copy(i)} onRemove={() => remove(i)}
              first={i === 0} last={i === qs.length - 1} roleOpts={roleOpts}
              drag={{
                over: dragOver === i && dragFrom !== i,
                onStart: (e) => { setDragFrom(i); try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); } catch (err) { /* ignore */ } },
                onEnd: () => { setDragFrom(null); setDragOver(null); },
                onOver: (e) => { if (dragFrom != null) { e.preventDefault(); if (dragOver !== i) setDragOver(i); } },
                onDrop: (e) => { e.preventDefault(); drop(i); },
              }} />
          );
        })}

        {qs.length === 0 && <div className="gtfm-none gtfm-none--build">No questions yet. Add the first one below.</div>}

        <div className="gtfm-addbar">
          <button type="button" className="gtfm-add" onClick={addQuestion}><QIcon d={I.plus} cls="gtfm-ico-sm" />Question</button>
          <button type="button" className="gtfm-add gtfm-add--soft" onClick={addSection}><QIcon d={QTYPE.section.ico} cls="gtfm-ico-sm" />Section</button>
        </div>

        <div className={'gtfm-savebar' + (dirty ? ' gtfm-savebar--on' : '')}>
          <span>{dirty ? 'Unsaved changes' : 'All changes saved'}</span>
          <div className="gtbn-spacer" />
          {dirty && <button type="button" className="gtc-btn-ghost" disabled={busy} onClick={() => { const s = JSON.parse(saved); setDraft(s); setActive(null); }}>Discard</button>}
          <button type="button" className="gtc-primary" disabled={busy || !dirty} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>

      <aside className="gtfm-preview">
        <div className="gtfm-preview-label">What people see</div>
        <div className="gtfm-preview-frame">
          <div className="gtfp-card gtfp-head gtfp-head--mini">
            <div className="gtfp-band" />
            <h1>{draft.title || 'Form title'}</h1>
            {draft.intro && <div className="gtfp-intro"><p>{draft.intro}</p></div>}
          </div>
          {qs.length ? <GtFormFields questions={qs.map((x) => ({ ...x, label: x.label || (x.type === 'section' ? 'Untitled section' : 'Untitled question'), options: x.options && x.options.length ? x.options.filter(Boolean) : x.options }))}
            answers={preview} onChange={(k, v) => setPreview((p) => ({ ...p, [k]: v }))} />
            : <div className="gtfm-preview-empty">Questions appear here as you add them.</div>}
          <div className="gtfp-card gtfp-submit gtfp-submit--mini"><button type="button" className="gtfp-primary" disabled>Submit</button></div>
        </div>
      </aside>
    </div>
  );
}

// ── Responses ──────────────────────────────────────────────────────────────
function Bars({ q, s }) {
  const keys = Object.keys(s.counts || {});
  const max = Math.max(1, ...keys.map((k) => s.counts[k]));
  const label = (k) => (q.type === 'yesno' || q.type === 'put_forward' ? (k === 'yes' ? 'Yes' : 'No') : k);
  return (
    <div className={'gtfm-bars' + (q.type === 'scale' ? ' gtfm-bars--scale' : '')}>
      {keys.map((k) => {
        const v = s.counts[k];
        const pct = s.answered ? Math.round((v / s.answered) * 100) : 0;
        return q.type === 'scale' ? (
          <div key={k} className="gtfm-col" title={v + ' chose ' + k}>
            <div className="gtfm-col-track"><i style={{ height: Math.round((v / max) * 100) + '%' }} /></div>
            <b>{k}</b><span>{v}</span>
          </div>
        ) : (
          <div key={k} className="gtfm-bar">
            <div className="gtfm-bar-top"><span>{label(k)}</span><b>{v}<em>{pct}%</em></b></div>
            <div className="gtfm-bar-track"><i style={{ width: Math.round((v / max) * 100) + '%' }} /></div>
          </div>
        );
      })}
    </div>
  );
}

function Highlight({ text, term }) {
  if (!term) return text;
  const lower = text.toLowerCase();
  const t = term.toLowerCase();
  const out = [];
  let i = 0;
  let at = lower.indexOf(t);
  while (at !== -1 && out.length < 40) {
    if (at > i) out.push(text.slice(i, at));
    out.push(<mark key={at}>{text.slice(at, at + t.length)}</mark>);
    i = at + t.length;
    at = lower.indexOf(t, i);
  }
  out.push(text.slice(i));
  return out;
}

// Every answer to one question, with who gave it. Loads 50 at a time.
function AnswersModal({ form, q, scope, total, authFetch, onClose }) {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [term, setTerm] = useState('');
  const [rows, setRows] = useState(null);
  const [count, setCount] = useState(total);
  const [more, setMore] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { const t = setTimeout(() => setTerm(text.trim()), 300); return () => clearTimeout(t); }, [text]);
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);
  const url = useCallback((off) => '/api/forms/' + form.id + '/answers?key=' + q.key + '&scope=' + encodeURIComponent(scope)
    + (term ? '&q=' + encodeURIComponent(term) : '') + '&offset=' + off, [form.id, q.key, scope, term]);
  useEffect(() => {
    let live = true;
    setRows(null);
    authFetch(url(0)).then((r) => { if (live) { setRows(r.rows); setCount(r.total); setError(null); } })
      .catch((e) => { if (live) { setRows([]); setError(e.message); } });
    return () => { live = false; };
  }, [authFetch, url]);
  async function loadMore() {
    setMore(true);
    try { const r = await authFetch(url(rows.length)); setRows((old) => old.concat(r.rows)); } catch (e) { setError(e.message); }
    setMore(false);
  }

  return (
    <div className="gtc-modal-backdrop" onClick={onClose}>
      <div className="gtc-modal gtfm-amodal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="gtc-modal-head gtfm-amodal-h">
          <div><span className="gtfm-amodal-k">{plural(count, 'answer', 'answers')}{term ? ' matching' : ''}</span><b>{q.label}</b></div>
          <button type="button" className="gtfm-iconbtn" aria-label="Close" onClick={onClose}><QIcon d={I.x} cls="gtfm-ico-sm" /></button>
        </div>
        <div className="gtfm-amodal-search">
          <input autoFocus className="gtsb-search" placeholder="Search answers or names" value={text} onChange={(e) => setText(e.target.value)} />
        </div>
        <div className="gtfm-amodal-list">
          {error && <div className="gtnt-banner gtnt-banner--rose">{error}</div>}
          {rows === null && <div className="gtfm-skel"><div className="gtfm-skel-row" /><div className="gtfm-skel-row" /></div>}
          {rows && rows.map((x) => (
            <div key={x.id} className="gtfm-arow">
              <p><Highlight text={answerText(q, x.value)} term={term} /></p>
              <div className="gtfm-arow-by">
                <button type="button" className="gtfm-name" onClick={() => { onClose(); navigate('/candidates/' + (x.candidate.ref || x.candidate.id)); }}>
                  <Highlight text={x.candidate.name || 'Unnamed'} term={term} />
                </button>
                <span>{relTime(x.answered_at)}{x.via === 'link' ? ' · shared link' + (x.src ? ' (' + x.src + ')' : '') : ''}</span>
              </div>
            </div>
          ))}
          {rows && !rows.length && !error && <div className="gtfm-none">{term ? 'No answers match.' : 'No answers yet.'}</div>}
          {rows && rows.length < count && (
            <button type="button" className="gtc-btn-ghost gtfm-more" disabled={more} onClick={loadMore}>{more ? 'Loading…' : 'Show more (' + (count - rows.length) + ' left)'}</button>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ q, s, onAll }) {
  if (!s) return null;
  return (
    <div className="gtfm-sum">
      <div className="gtfm-sum-h"><b>{q.label}</b><span>{plural(s.answered, 'answer', 'answers')}{s.average != null ? ' · average ' + s.average : ''}</span></div>
      {s.stats && (
        <div className="gtfm-nums">
          <div><b>{s.stats.median.toLocaleString('en-GB')}</b><span>Median</span></div>
          <div><b>{s.stats.min.toLocaleString('en-GB')}</b><span>Lowest</span></div>
          <div><b>{s.stats.max.toLocaleString('en-GB')}</b><span>Highest</span></div>
        </div>
      )}
      {s.counts ? <Bars q={q} s={s} /> : (
        s.latest && s.latest.length ? (
          <>
            <ul className="gtfm-quotes">{s.latest.slice(0, 3).map((v, i) => <li key={i}>{answerText(q, v)}</li>)}</ul>
            <button type="button" className="gtfm-allbtn" onClick={onAll}>{s.answered > 3 ? 'See all ' + s.answered + ' answers' : 'See who answered'}</button>
          </>
        ) : <div className="gtfm-sum-none">No answers yet.</div>
      )}
    </div>
  );
}

const SHOW = [['all', 'Everyone'], ['answered', 'Answered'], ['waiting', 'Waiting'], ['undelivered', 'Not delivered']];
const ROW_PILL = { answered: 'gtcf-p--green', sent: 'gtcf-p--amber', queued: 'gtcf-p--slate', blocked: 'gtcf-p--rose', failed: 'gtcf-p--rose', link: 'gtcf-p--slate' };

function csvCell(v) { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; }

function Responses({ form, sends, link, authFetch, flash, onChanged }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const scope = params.get('scope') || 'all';
  const [show, setShow] = useState('all');
  const [qtext, setQtext] = useState('');
  const [debq, setDebq] = useState('');
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState({});
  const [fresh, setFresh] = useState({});
  const [more, setMore] = useState(false);
  const [view, setView] = useState('summary');
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const [allFor, setAllFor] = useState(null);
  const lastCheck = useRef(null);
  const rowsLen = useRef(0);
  rowsLen.current = rows.length;

  useEffect(() => { const t = setTimeout(() => setDebq(qtext.trim()), 300); return () => clearTimeout(t); }, [qtext]);

  const url = useCallback((offset, limit) => '/api/forms/' + form.id + '/responses?scope=' + encodeURIComponent(scope) + '&show=' + show
    + (debq ? '&q=' + encodeURIComponent(debq) : '') + '&offset=' + offset + (limit ? '&limit=' + limit : ''), [form.id, scope, show, debq]);

  const load = useCallback(async (quiet) => {
    try {
      const r = await authFetch(url(0));
      const prev = lastCheck.current;
      if (quiet && prev) {
        const n = {};
        r.rows.forEach((x) => { if (x.answered_at && x.answered_at > prev) n[x.id] = true; });
        if (Object.keys(n).length) { setFresh(n); setTimeout(() => setFresh({}), 4000); }
      }
      lastCheck.current = r.checked_at;
      setData(r);
      setRows((old) => (quiet && old.length > r.rows.length ? r.rows.concat(old.slice(r.rows.length)) : r.rows));
      setError(null);
    } catch (e) { if (!quiet) setError(e.message); }
  }, [authFetch, url]);

  useEffect(() => { setData(null); setRows([]); lastCheck.current = null; load(false); }, [load]);

  // Live: check every 8 seconds while the page is in view.
  useEffect(() => {
    const t = setInterval(() => { if (document.visibilityState === 'visible') { load(true); setTick((x) => x + 1); } }, 8000);
    const clock = setInterval(() => setTick((x) => x + 1), 1000);
    return () => { clearInterval(t); clearInterval(clock); };
  }, [load]);

  async function loadMore() {
    setMore(true);
    try { const r = await authFetch(url(rowsLen.current)); setRows((old) => old.concat(r.rows)); }
    catch (e) { flash(e.message); }
    setMore(false);
  }

  async function exportCsv() {
    setBusy(true);
    try {
      const r = await authFetch('/api/forms/' + form.id + '/responses?scope=' + encodeURIComponent(scope) + '&show=' + show + '&limit=all');
      const qs = (r.questions || []).filter((x) => x.type !== 'section');
      const head = ['Name', 'Email', 'ID', 'Status', 'How', 'Source', 'Sent', 'Answered'].concat(qs.map((x) => x.label));
      const lines = [head.map(csvCell).join(',')];
      r.rows.forEach((x) => {
        const c = x.candidate || {};
        lines.push([c.name, c.email, c.ref, x.state_label, x.via === 'link' ? 'Shared link' : 'Email', x.src || '',
          x.sent_at ? ukDate(x.sent_at) : '', x.answered_at ? ukDate(x.answered_at) : '']
          .concat(qs.map((qq) => answerText(qq, x.answers && x.answers[qq.key]))).map(csvCell).join(','));
      });
      const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = form.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase() + '-responses-' + new Date().toISOString().slice(0, 10) + '.csv';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (e) { flash(e.message); }
    setBusy(false);
  }

  const send = sends.find((s) => String(s.id) === scope) || null;
  async function remind() {
    setBusy(true);
    try {
      const r = await authFetch('/api/forms/sends/' + send.id + '/remind', { method: 'POST', headers: JSON_HEADERS, body: '{}' });
      flash(r.queued ? 'Reminding ' + plural(r.queued, 'person', 'people') : 'Nobody to remind right now');
      onChanged();
    } catch (e) { flash(e.message); }
    setBusy(false);
  }

  const scopeOpts = [{ value: 'all', label: 'All responses' }]
    .concat(link.responses || form.link_enabled ? [{ value: 'link', label: 'Shared link' }] : [])
    .concat(sends.map((s) => ({ value: String(s.id), label: (s.source_label || 'Email') + ' · ' + ukDate(s.created_at) + ' · ' + s.total })));
  const setScope = (v) => { const p = new URLSearchParams(params); p.set('tab', 'responses'); p.set('scope', v); setParams(p, { replace: true }); };

  const c = data ? data.counts : null;
  // tick re-renders this once a second so "checked 4s ago" stays true.
  const secs = tick >= 0 && lastCheck.current ? Math.max(0, Math.round((Date.now() - new Date(lastCheck.current).getTime()) / 1000)) : null;
  const sendingNow = sends.some((s) => s.status === 'sending');
  const questions = data ? data.questions.filter((x) => x.type !== 'section') : [];

  return (
    <div className="gtfm-resp">
      <div className="gtfm-rbar">
        <div className="gtfm-rscope"><GtSelect value={scope} options={scopeOpts} onChange={setScope} /></div>
        <span className="gtfm-live" title="Checks for new answers every few seconds"><i />Live{secs != null ? ' · checked ' + (secs < 5 ? 'just now' : secs + 's ago') : ''}</span>
        <div className="gtbn-spacer" />
        {send && send.waiting > 0 && <button type="button" className="gtc-btn-ghost" disabled={busy} onClick={remind}><QIcon d={I.bell} cls="gtfm-ico-sm" />Remind {send.waiting}</button>}
        <button type="button" className="gtc-btn-ghost" disabled={busy || !c || !c.total} onClick={exportCsv}><QIcon d={I.dl} cls="gtfm-ico-sm" />Export</button>
      </div>

      {error && <div className="gtnt-banner gtnt-banner--rose">{error}</div>}
      {sendingNow && <div className="gtfm-note">Emails are still going out. Numbers update as they do.</div>}

      {c && (
        <div className="gtfm-tiles">
          {scope === 'link' ? (
            <>
              <div className="gtfm-tile"><b>{link.views}</b><span>Link views</span></div>
              <div className="gtfm-tile gtfm-tile--go"><b>{c.answered}</b><span>Responses</span></div>
              <div className="gtfm-tile"><b>{link.views ? Math.round((link.submits / link.views) * 100) + '%' : '–'}</b><span>Of visitors answered</span></div>
            </>
          ) : (
            <>
              {c.asked > 0 && <div className="gtfm-tile"><b>{c.asked}</b><span>Emailed</span></div>}
              {c.asked > 0 && <div className="gtfm-tile"><b>{c.delivered}</b><span>Delivered</span></div>}
              {c.asked > 0 && <div className="gtfm-tile"><b>{c.opened}</b><span>Opened</span></div>}
              <div className="gtfm-tile gtfm-tile--go"><b>{c.answered}</b><span>Answered</span></div>
              {c.asked > 0 && <div className="gtfm-tile"><b>{c.delivered ? Math.round(((c.answered - (scope === 'all' ? (link.responses || 0) : 0)) / c.delivered) * 100) + '%' : '–'}</b><span>Response rate</span></div>}
            </>
          )}
          {c.last_answer_at && <div className="gtfm-tile gtfm-tile--soft"><b>{relTime(c.last_answer_at)}</b><span>Latest answer</span></div>}
        </div>
      )}

      {data && (
        <div className="gtfm-seg gtfm-seg--view" role="tablist">
          {[['summary', 'Summary'], ['people', 'People']].map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={view === k} className={'gtfm-seg-b' + (view === k ? ' gtfm-seg-b--on' : '')} onClick={() => setView(k)}>{l}</button>
          ))}
        </div>
      )}

      {!data && !error && <div className="gtfm-skel"><div className="gtfm-skel-row" /><div className="gtfm-skel-row" /></div>}

      {data && view === 'summary' && (
        c.answered === 0 ? (
          <div className="gtfm-empty gtfm-empty--soft">
            <h3>No answers yet</h3>
            <p>{c.total ? 'Answers appear here the moment they come in. This page keeps itself up to date.' : 'Send this form from Candidates, a bench or a role, or switch on its shared link under Share and send.'}</p>
          </div>
        ) : (
          <div className="gtfm-sums">{questions.map((qq) => <SummaryCard key={qq.key} q={qq} s={data.summary[qq.key]} onAll={() => setAllFor(qq)} />)}</div>
        )
      )}

      {allFor && data && data.summary[allFor.key] && (
        <AnswersModal form={form} q={allFor} scope={scope} total={data.summary[allFor.key].answered} authFetch={authFetch} onClose={() => setAllFor(null)} />
      )}

      {data && view === 'people' && (
        <>
          <div className="gtfm-pbar">
            <div className="gtfm-chips">
              {SHOW.map(([k, l]) => (
                <button key={k} type="button" className={'gtfm-chip' + (show === k ? ' gtfm-chip--on' : '')} onClick={() => setShow(k)}>
                  {l}<em>{k === 'all' ? c.total : c[k]}</em>
                </button>
              ))}
            </div>
            <input className="gtsb-search gtfm-psearch" placeholder="Search name, email or ID" value={qtext} onChange={(e) => setQtext(e.target.value)} />
          </div>
          <div className="gtfm-people">
            {rows.map((x) => {
              const p = x.candidate || {};
              const isOpen = !!open[x.id];
              return (
                <div key={x.id} className={'gtfm-row' + (isOpen ? ' gtfm-row--open' : '') + (fresh[x.id] ? ' gtfm-row--new' : '')}>
                  <div className="gtfm-row-h" role="button" tabIndex={0} onClick={() => x.answered_at && setOpen((o) => ({ ...o, [x.id]: !o[x.id] }))}
                    onKeyDown={(e) => { if (e.key === 'Enter' && x.answered_at) setOpen((o) => ({ ...o, [x.id]: !o[x.id] })); }}>
                    <span className="gtfm-av" style={{ background: avatarColour(p.name) }}>{initials(p.name)}</span>
                    <div className="gtfm-row-who">
                      <button type="button" className="gtfm-name" onClick={(e) => { e.stopPropagation(); navigate('/candidates/' + (p.ref || p.id)); }}>{p.name || 'Unnamed'}</button>
                      <span>{p.email || ''}{x.via === 'link' ? ' · shared link' + (x.src ? ' (' + x.src + ')' : '') : ''}</span>
                    </div>
                    <span className={'gtcf-p ' + (ROW_PILL[x.state] || 'gtcf-p--slate')}>{x.state_label}</span>
                    <span className="gtfm-row-when" title={x.detail || ''}>
                      {x.answered_at ? relTime(x.answered_at) : x.detail ? x.detail : x.opened_at ? 'Opened ' + relTime(x.opened_at) : x.sent_at ? 'Sent ' + relTime(x.sent_at) : ''}
                    </span>
                    <span className="gtfm-row-chev">{x.answered_at ? <QIcon d={I.chev} cls={'gtfm-ico-sm' + (isOpen ? ' gtfm-rot' : '')} /> : null}</span>
                  </div>
                  {isOpen && <div className="gtfm-row-b"><GtAnswerList questions={data.questions} answers={x.answers} /></div>}
                </div>
              );
            })}
            {!rows.length && <div className="gtfm-none">Nobody here.</div>}
          </div>
          {rows.length < data.matching && (
            <button type="button" className="gtc-btn-ghost gtfm-more" disabled={more} onClick={loadMore}>{more ? 'Loading…' : 'Show more (' + (data.matching - rows.length) + ' left)'}</button>
          )}
        </>
      )}
    </div>
  );
}

// ── Share and send ─────────────────────────────────────────────────────────
function Share({ form, sends, link, authFetch, flash, onChanged, openScope }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [src, setSrc] = useState('');
  const archived = form.status === 'archived';
  const tagged = form.public_url && src.trim() ? form.public_url + '?src=' + encodeURIComponent(src.trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '-')) : form.public_url;

  async function toggle(on) {
    setBusy(true);
    try { await authFetch('/api/forms/' + form.id + '/link', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ enabled: on }) }); flash(on ? 'Link is on' : 'Link is off'); onChanged(); }
    catch (e) { flash(e.message); }
    setBusy(false);
  }
  const doCopy = (t) => copyText(t).then(() => flash('Link copied'));

  return (
    <div className="gtfm-share">
      <section className="gtfm-panel">
        <div className="gtfm-panel-h">
          <div><h3>Shared link</h3><p>One link anyone can open: post it, message it, put it on a page. People give their name and email, and their answers join their record. Someone new is added to the pool with their consent.</p></div>
          <Switch on={form.link_enabled} onChange={(v) => !busy && !archived && toggle(v)} label={form.link_enabled ? 'On' : 'Off'} />
        </div>
        {archived && <div className="gtfm-note">Restore this form to share it.</div>}
        {form.link_enabled && form.public_url && (
          <>
            <div className="gtfm-url">
              <QIcon d={I.link} cls="gtfm-ico-sm" />
              <input readOnly value={tagged} onFocus={(e) => e.target.select()} />
              <button type="button" className="gtc-primary" onClick={() => doCopy(tagged)}>Copy</button>
              <a className="gtc-btn-ghost gtfm-open" href={tagged} target="_blank" rel="noreferrer"><QIcon d={I.open} cls="gtfm-ico-sm" />Open</a>
            </div>
            <div className="gtfm-src">
              <span>Track where people come from</span>
              <div className="gtfm-src-row">
                <input value={src} maxLength={40} placeholder="e.g. linkedin" onChange={(e) => setSrc(e.target.value)} />
                {['linkedin', 'email', 'whatsapp', 'website'].map((s) => (
                  <button key={s} type="button" className={'gtfm-chip' + (src === s ? ' gtfm-chip--on' : '')} onClick={() => setSrc(src === s ? '' : s)}>{s}</button>
                ))}
              </div>
            </div>
          </>
        )}
        {!form.link_enabled && form.public_url && <div className="gtfm-muted">The link is off. Anyone who opens it sees that the form is not taking responses. Switching it on brings the same link back.</div>}
        {(link.views > 0 || link.responses > 0) && (
          <div className="gtfm-srcs">
            <div className="gtfm-srcs-h"><span>Source</span><span>Views</span><span>Responses</span><span>Answered</span></div>
            {link.sources.map((s) => (
              <div key={s.src || '-'} className="gtfm-srcs-r">
                <span>{s.src || 'No tag'}</span><b>{s.views}</b><b>{s.responses}</b>
                <span className="gtfm-srcs-rate"><div className="gtfm-minibar"><i style={{ width: (s.views ? Math.min(100, Math.round((s.responses / s.views) * 100)) : 0) + '%' }} /></div>{s.views ? Math.round((s.responses / s.views) * 100) + '%' : '–'}</span>
              </div>
            ))}
            <button type="button" className="gtfm-linkbtn" onClick={() => openScope('link')}>See link responses</button>
          </div>
        )}
      </section>

      <section className="gtfm-panel">
        <div className="gtfm-panel-h">
          <div><h3>Send by email</h3><p>Tick people on Candidates, a bench or a role's pipeline, then press <b>Send form</b> and attach this one. Each person gets their own link, with your message above it.</p></div>
        </div>
        <div className="gtfm-goto">
          <button type="button" className="gtc-btn-ghost" onClick={() => navigate('/candidates')}>Candidates</button>
          <button type="button" className="gtc-btn-ghost" onClick={() => navigate('/benches')}>Benches</button>
          <button type="button" className="gtc-btn-ghost" onClick={() => navigate('/roles')}>Roles</button>
        </div>
        {sends.length > 0 && (
          <div className="gtfm-sends">
            {sends.map((s) => {
              const rate = s.delivered ? Math.round((s.answered / s.delivered) * 100) : 0;
              return (
                <button type="button" key={s.id} className="gtfm-send" onClick={() => openScope(String(s.id))}>
                  <span className="gtfm-send-ico"><QIcon d={I.send} cls="gtfm-ico-sm" /></span>
                  <span className="gtfm-send-main">
                    <b>{s.source_label || 'Email'}</b>
                    <span>{ukDate(s.created_at)}{s.sent_by_name ? ' · ' + s.sent_by_name : ''}{s.closes_at ? ' · closes ' + ukDate(s.closes_at) : ''}{s.status === 'sending' ? ' · sending now' : ''}</span>
                  </span>
                  <span className="gtfm-send-stats">
                    <span><b>{s.delivered}</b> delivered</span>
                    <span><b>{s.answered}</b> answered</span>
                    <span className="gtfm-send-rate"><div className="gtfm-minibar"><i style={{ width: rate + '%' }} /></div>{rate}%</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
